import { CustomKeyAttributes } from '@loyalty/shared-common/profile-api-client';
import { Fields } from '@react-awesome-query-builder/core';
import { BaseConfigSettings } from '../fields';
import { excludedOperators, referralExcludedOperators } from '../settings';
import { profileFields } from './profile-fields';
import { shouldBeApositiveNumber } from './should-be-apositive-number';

export function ecommercePurchaseFields(
  settings: BaseConfigSettings,
  customKeys?: CustomKeyAttributes[],
): Fields {
  const { t } = settings;
  return {
    event: {
      label: t('rule.criteria.event'),
      type: '!group',
      subfields: {
        referralCode: {
          label: t('rule.criteria.ecommerce.referral.used'),
          type: 'text',
          valueSources: ['value'],
          excludeOperators: referralExcludedOperators,
        },
        amountVatExcluded: {
          label: t('rule.criteria.ecommerce.amount.vat.excluded'),
          type: 'number',
          valueSources: ['value'],
          fieldSettings: {
            validateValue: shouldBeApositiveNumber,
          },
        },
        amountVatIncluded: {
          label: t('rule.criteria.ecommerce.amount.vat.included'),
          type: 'number',
          valueSources: ['value'],
          fieldSettings: {
            validateValue: shouldBeApositiveNumber,
          },
        },
        shippingCostVatIncluded: {
          label: t('rule.criteria.ecommerce.shipping'),
          type: 'number',
          valueSources: ['value'],
          fieldSettings: {
            validateValue: shouldBeApositiveNumber,
          },
        },
        totalAmountVatIncluded: {
          label: t('rule.criteria.ecommerce.total'),
          type: 'number',
          valueSources: ['value'],
          fieldSettings: {
            validateValue: shouldBeApositiveNumber,
          },
        },
        currency: {
          label: t('rule.criteria.ecommerce.currency'),
          type: 'select',
          valueSources: ['value'],
          fieldSettings: {
            listValues: [
              { value: 'EUR', title: 'EUR' },
              { value: 'USD', title: 'USD' },
              { value: 'GBP', title: 'GBP' },
              { value: 'CHF', title: 'CHF' },
            ],
          },
        },
        basket: {
          label: t('rule.criteria.ecommerce.basket'),
          type: '!group',
          subfields: {
            productName: {
              label: t('rule.criteria.ecommerce.product.name'),
              type: 'text',
              excludeOperators: excludedOperators,
              valueSources: ['value'],
            },
            productCode: {
              label: t('rule.criteria.ecommerce.product.code'),
              type: 'text',
              excludeOperators: excludedOperators,
              valueSources: ['value'],
            },
            productVatExcluded: {
              label: t('rule.criteria.ecommerce.product.vat.excluded'),
              type: 'number',
              fieldSettings: {
                validateValue: shouldBeApositiveNumber,
              },
              valueSources: ['value'],
              preferWidgets: ['number'],
            },
            productVatIncluded: {
              label: t('rule.criteria.ecommerce.product.vat.included'),
              type: 'number',
              fieldSettings: {
                validateValue: shouldBeApositiveNumber,
              },
              valueSources: ['value'],
              preferWidgets: ['number'],
            },
            quantity: {
              label: t('rule.criteria.ecommerce.quantity'),
              type: 'number',
              fieldSettings: {
                validateValue: shouldBeApositiveNumber,
              },
              valueSources: ['value'],
              preferWidgets: ['number'],
            },
          },
        },
        basketTotal: {
          label: t('rule.criteria.ecommerce.basket.total'),
          type: 'number',
          valueSources: ['value'],
          fieldSettings: {
            validateValue: shouldBeApositiveNumber,
          },
        },
        quantityTotal: {
          label: t('rule.criteria.ecommerce.quantity.total'),
          type: 'number',
          fieldSettings: {
            validateValue: shouldBeApositiveNumber,
          },
          valueSources: ['value'],
        },
        tags: {
          label: t('rule.criteria.tags'),
          type: '!group',
          subfields: {
            name: {
              label: t('rule.criteria.name'),
              type: 'text',
              excludeOperators: excludedOperators,
              valueSources: ['value'],
            },
            value: {
              label: t('rule.criteria.value'),
              type: 'text',
              excludeOperators: excludedOperators,
              valueSources: ['value'],
            },
          },
        },
        couponUsed: {
          label: t('rule.criteria.ecommerce.coupon'),
          type: '!group',
          subfields: {
            code: {
              type: 'text',
              excludeOperators: excludedOperators,
              label: t('rule.criteria.code'),
              valueSources: ['value'],
            },
          },
        },
        timestamp: {
          label: settings.t('rule.criteria.date'),
          type: 'datetime',
          preferWidgets: ['date'],
        },
      },
    },
    profile: {
      label: t('rule.criteria.profile'),
      type: '!group',
      subfields: profileFields(settings, customKeys),
    },
  };
}
